'use client'

import { useCallback } from 'react'

import { useCapabilities } from '@/hooks/use-capabilities'
import { useUpgradeHint } from '@/hooks/use-upgrade-hint'
import { isVenueAllowed } from '@/lib/billing/capabilities'
import { TradeVenue } from '@/types/generated/common/types'

export type VenueGate = {
  /** Whether the current plan may connect an account on this venue. */
  allowed: boolean
  /**
   * Label of the cheapest plan that unlocks the venue (e.g. "Basic", "Pro"),
   * for a "Requires <plan>" upsell. Undefined while the catalog is loading or
   * when no plan offers the venue — fall back to a generic upgrade prompt.
   */
  upgradePlanLabel?: string
}

/**
 * Resolves per-venue access for the account-create flow: the lock state comes
 * from the user's `capabilities` (`get_subscription`, permissive while loading —
 * see `useCapabilities`), and the upgrade target from the public `get_plans`
 * catalog. Keeping both here lets the venue picker both gate and upsell without
 * hardcoding the tier table.
 */
export function useVenueGate(
  options: { allowWhileLoading?: boolean } = {}
): (venue: TradeVenue) => VenueGate {
  const { isVenueAllowed: isAllowed } = useCapabilities(options)
  const upgradeHint = useUpgradeHint()

  return useCallback(
    (venue: TradeVenue): VenueGate => {
      if (isAllowed(venue)) return { allowed: true }
      return {
        allowed: false,
        upgradePlanLabel: upgradeHint((caps) => isVenueAllowed(caps, venue)),
      }
    },
    [isAllowed, upgradeHint]
  )
}
